(() => {
  'use strict';

  const placeholder = 'time_to_cut_off';
  const installRetryLimit = 240;
  const installRetryDelay = 25;
  let installAttempts = 0;
  let pendingEstimateUpdate = null;
  let pendingHistorySync = null;

  const cartRoute = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;

  const readJsonResponse = async (response, operation) => {
    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      if (response.ok) {
        throw new Error(`${operation} returned an invalid JSON response.`);
      }
    }

    if (!response.ok || Number(payload?.status) >= 400) {
      const message = payload?.description
        || payload?.message
        || `${operation} failed with HTTP ${response.status}.`;
      const requestError = new Error(message);

      requestError.status = response.status;
      requestError.payload = payload;
      throw requestError;
    }

    return payload;
  };

  const parseItemProperties = (element) => {
    try {
      return JSON.parse(element.getAttribute('x-data-properties') || '{}');
    } catch (error) {
      console.error('[CWS cart estimate] Invalid line-item properties.', error);
      return null;
    }
  };

  const getCartItemElement = (itemKey) => Array.from(
    document.querySelectorAll('.cart-item[x-data-key]'),
  ).find((element) => element.getAttribute('x-data-key') === itemKey);

  const getPropertyUpdates = (cart) => {
    const estimateNotice = window.Alpine?.store('xEstimateDelivery')?.noti;

    if (!estimateNotice) {
      return [];
    }

    return cart.items.reduce((updates, item) => {
      const itemElement = getCartItemElement(item.key);

      if (!itemElement) {
        return updates;
      }

      const properties = parseItemProperties(itemElement);

      if (!properties) {
        return updates;
      }

      let changed = false;
      const nextProperties = Object.fromEntries(Object.entries(properties).map(([key, value]) => {
        if (typeof value !== 'string' || !value.includes(placeholder)) {
          return [key, value];
        }

        changed = true;
        return [key, value.replaceAll(placeholder, estimateNotice)];
      }));

      if (changed) {
        updates.push({
          key: item.key,
          properties: nextProperties,
          quantity: item.quantity,
        });
      }

      return updates;
    }, []);
  };

  const getSections = (cartHelper) => {
    try {
      return cartHelper.getSectionsToRender?.() || [];
    } catch (error) {
      console.error('[CWS cart estimate] Unable to resolve cart sections.', error);
      return [];
    }
  };

  const getSectionInnerHtml = (html, selector) => {
    const sectionDocument = new DOMParser().parseFromString(html, 'text/html');
    return sectionDocument.querySelector(selector)?.innerHTML || null;
  };

  const renderSections = (payload, sections) => {
    if (!payload?.sections) {
      return;
    }

    sections.forEach((section) => {
      const sectionElement = document.querySelector(section.selector);
      const sectionHtml = payload.sections[section.id];

      if (!sectionElement || !sectionHtml) {
        return;
      }

      const innerHtml = getSectionInnerHtml(sectionHtml, section.selector);

      if (innerHtml !== null) {
        sectionElement.innerHTML = innerHtml;
      }
    });
  };

  const reportEstimateError = (error) => {
    const detail = {
      message: error?.message || 'Unable to refresh cart delivery estimates.',
      payload: error?.payload || null,
      status: error?.status || 0,
    };

    console.error('[CWS cart estimate] Safe update failed; cart items were preserved.', detail);
    document.dispatchEvent(new CustomEvent('cws:cart:estimate-error', { detail }));
  };

  const updateCartCountElements = (itemCount) => {
    ['#cart-icon-bubble', '#mobile-cart-icon-bubble'].forEach((selector) => {
      const cartCountElement = document.querySelector(selector);
      const countLabel = cartCountElement?.querySelector('span');

      if (!cartCountElement || !countLabel) {
        return;
      }

      cartCountElement.dataset.value = String(itemCount);
      countLabel.textContent = itemCount < 100 ? String(itemCount) : '99+';
    });
  };

  const syncCartAfterHistoryRestore = (event = {}) => {
    scheduleInstall();

    const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
    const legacyNavigation = window.performance?.navigation;
    const isHistoryRestore = event.persisted
      || event.type === 'popstate'
      || navigationEntry?.type === 'back_forward'
      || legacyNavigation?.type === legacyNavigation?.TYPE_BACK_FORWARD;

    if (!isHistoryRestore) {
      return Promise.resolve();
    }

    if (!pendingHistorySync) {
      document.documentElement.dataset.cwsCartHistorySync = 'pending';
      pendingHistorySync = (async () => {
        try {
          const cartResponse = await fetch(cartRoute('cart.js'), {
            headers: { Accept: 'application/json' },
          });
          const cart = await readJsonResponse(cartResponse, 'Cart history sync');
          const cartHelper = window.Alpine?.store('xCartHelper');
          const miniCart = window.Alpine?.store('xMiniCart');

          updateCartCountElements(cart.item_count);

          if (cartHelper) {
            cartHelper.currentItemCount = cart.item_count;
          }

          if (typeof miniCart?.reLoad === 'function') {
            miniCart.reLoad();
            miniCart.needReload = true;
          }

          document.documentElement.dataset.cwsCartHistorySync = 'complete';
          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        } catch (error) {
          document.documentElement.dataset.cwsCartHistorySync = 'failed';
          reportEstimateError(error);
        } finally {
          pendingHistorySync = null;
        }
      })();
    }

    return pendingHistorySync;
  };

  const runEstimateUpdate = async (cartHelper) => {
    window.updatingEstimate = true;

    try {
      const cartResponse = await fetch(cartRoute('cart.js'), {
        headers: { Accept: 'application/json' },
      });
      const cart = await readJsonResponse(cartResponse, 'Cart read');
      const propertyUpdates = getPropertyUpdates(cart);

      if (!propertyUpdates.length) {
        cartHelper.needUpdateEstimated = false;
        return;
      }

      const sections = getSections(cartHelper);
      const sectionIds = [...new Set(sections.map((section) => section.id))];
      let latestPayload = null;

      for (let index = 0; index < propertyUpdates.length; index += 1) {
        const update = propertyUpdates[index];
        const isLastUpdate = index === propertyUpdates.length - 1;
        const requestBody = {
          id: update.key,
          properties: update.properties,
          quantity: update.quantity,
        };

        if (isLastUpdate && sectionIds.length) {
          requestBody.sections = sectionIds;
          requestBody.sections_url = window.location.pathname;
        }

        const changeResponse = await fetch(cartRoute('cart/change.js'), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        latestPayload = await readJsonResponse(changeResponse, 'Delivery estimate update');
      }

      renderSections(latestPayload, sections);
      cartHelper.currentItemCount = latestPayload?.item_count ?? cart.item_count;
      cartHelper.needUpdateEstimated = false;
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
    } catch (error) {
      reportEstimateError(error);
    } finally {
      window.updatingEstimate = false;
    }
  };

  const installSafeCartEstimateUpdate = () => {
    const cartHelper = window.Alpine?.store('xCartHelper');

    if (!cartHelper) {
      return false;
    }

    if (cartHelper.updateEstimateShippingFull?.cwsSafeCartUpdate) {
      return true;
    }

    const originalWaitForEstimateUpdate = cartHelper.waitForEstimateUpdate?.bind(cartHelper);

    const safeUpdateEstimateShippingFull = function () {
      if (!this.needUpdateEstimated) {
        window.updatingEstimate = false;
        return Promise.resolve();
      }

      const queryString = window.location.search;
      const sharedCartPending = queryString.includes('share_cart:true')
        && !window.Alpine?.store('xCartShare')?.shared;

      if (sharedCartPending) {
        window.updatingEstimate = false;
        return Promise.resolve();
      }

      if (!pendingEstimateUpdate) {
        const update = runEstimateUpdate(this);

        pendingEstimateUpdate = update.finally(() => {
          pendingEstimateUpdate = null;
        });
      }

      return pendingEstimateUpdate;
    };

    safeUpdateEstimateShippingFull.cwsSafeCartUpdate = true;
    cartHelper.updateEstimateShippingFull = safeUpdateEstimateShippingFull;
    cartHelper.waitForEstimateUpdate = () => {
      if (pendingEstimateUpdate) {
        return pendingEstimateUpdate;
      }

      return originalWaitForEstimateUpdate?.() || Promise.resolve();
    };
    cartHelper.waitForEstimateUpdate.cwsSafeCartUpdate = true;

    window.CWSCartEstimateSafety = {
      installed: true,
      version: '1.0.0',
    };

    document.dispatchEvent(new CustomEvent('cws:cart:estimate-safety-installed'));
    return true;
  };

  const scheduleInstall = () => {
    if (installSafeCartEstimateUpdate() || installAttempts >= installRetryLimit) {
      return;
    }

    installAttempts += 1;
    window.setTimeout(scheduleInstall, installRetryDelay);
  };

  document.addEventListener('alpine:init', () => queueMicrotask(scheduleInstall), { once: true });
  document.addEventListener('alpine:initialized', scheduleInstall, { once: true });
  window.addEventListener('pageshow', syncCartAfterHistoryRestore);
  window.addEventListener('popstate', syncCartAfterHistoryRestore);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInstall, { once: true });
  } else {
    scheduleInstall();
  }
})();
